import apiFetch from '@wordpress/api-fetch';
import SortableSelect from '@components/sortable-multi-select';
import { addQueryArgs } from '@wordpress/url';
import { cacheMiddleware } from '@components/api-call-cache-middleware';

function registerTagReorderingPlugin({
  i18n: { __ },
  element: { useState, useEffect },
  data: { withSelect, dispatch },
  editor: { PluginDocumentSettingPanel },
  hooks: { applyFilters },
}) {
  const TagReorderingSettings = ({ postMeta, currentPostId, postType }) => {
    //NOSONAR
    const {
      selected_taxonomy_terms: selectedTaxonomyTermsMetaValue,
      selected_primary_terms: selectedPrimaryTerms,
    } = postMeta;

    const [selectedTaxonomyTerms, setSelectedTaxonomyTerms] = useState(
      selectedTaxonomyTermsMetaValue || [],
    );

    const [selectedTaxonomyTermsIds, setSelectedTaxonomyTermsIds] = useState(
      selectedTaxonomyTermsMetaValue || [],
    );

    const [primaryTermIds, setPrimaryTermIds] = useState(
      selectedPrimaryTerms || [],
    );
    const [primaryTerms, setPrimaryTerms] = useState(
      selectedPrimaryTerms || [],
    );

    const [taxonomiesForPostType, setTaxonomiesForPostType] = useState([]);

    useEffect(() => {
      apiFetch.use(cacheMiddleware);
      apiFetch({
        path: addQueryArgs(`/api/b3rg/getPostTypeTaxonomyTerms`, {
          postType,
          currentPostId,
        }),
        cachingEnabled: true,
      })
        .then((taxonomiesForPostType) => {
          setTaxonomiesForPostType(taxonomiesForPostType);

          if (taxonomiesForPostType?.length) {
            const selectedTaxonomyTerms = [];
            const primaryTaxonomyTerms = [];
            taxonomiesForPostType.forEach(
              ({ selectedTerms, slug, selectedPrimaryTerms }) => {
                selectedTaxonomyTerms[slug] = selectedTerms;
                primaryTaxonomyTerms[slug] = selectedPrimaryTerms;
              },
            );

            setSelectedTaxonomyTerms(selectedTaxonomyTerms);
            setPrimaryTerms(primaryTaxonomyTerms);
          }
        })
        .catch((e) => {
          console.log(e); //eslint-disable-line
          setTaxonomiesForPostType([]);
        });
    }, []); //eslint-disable-line

    const updateMeta = (field, value) => {
      // Save value to metadata.
      dispatch('core/editor').editPost({
        meta: {
          [field]: value,
        },
      });
    };

    const resetPrimaryTaxonomyTerm = (
      taxonomySlug,
      updatedSelectedTaxonomyTermsIds,
    ) => {
      const intersection = primaryTermIds[taxonomySlug]?.length
        ? primaryTermIds[taxonomySlug].filter((primaryTermId) =>
            updatedSelectedTaxonomyTermsIds[taxonomySlug].includes(
              parseInt(primaryTermId),
            ),
          )
        : [];

      if (!intersection.length) {
        const updatedSelectedPrimaryTermIds = {
          ...primaryTermIds,
          [taxonomySlug]: '',
        };

        const updatedSelectedPrimaryTerms = {
          ...primaryTerms,
          [taxonomySlug]: '',
        };

        setPrimaryTermIds(updatedSelectedPrimaryTermIds);
        setPrimaryTerms(updatedSelectedPrimaryTerms);

        updateMeta('selected_primary_terms', updatedSelectedPrimaryTermIds);
      }
    };

    const singleSelectTaxonomies = applyFilters(
      'b3rg_tag_reordering_single_select_taxonomies',
      taxonomiesForPostType,
    );

    const singleSelectTaxonomiesForPostType =
      singleSelectTaxonomies[postType] || [];

    const primaryTermTaxonomies = applyFilters(
      'b3rg_primary_term_enabled_taxonomies',
      taxonomiesForPostType,
    );

    const primaryTermTaxonomiesForPostType =
      primaryTermTaxonomies[postType] || [];

    const reOrderingTemplate = taxonomiesForPostType?.length
      ? taxonomiesForPostType.map((taxonomy, index) => {
          const taxonomySlug = taxonomy.slug;
          const tagElementKey = `tag-${index}`;
          const primaryTermElementKey = `primary-${index}`;
          //remove default taxonomy terms select dropdown from setting panel
          dispatch('core/editor').removeEditorPanel(
            `taxonomy-panel-${taxonomy.slug}`,
          );

          const isMultiSelect =
            !singleSelectTaxonomiesForPostType.includes(taxonomySlug);
          return (
            <>
              <div className="b3rg-tag-reordering" key={tagElementKey}>
                <label
                  className={'b3rg-tag-reordering__label'}
                  htmlFor="taxonomy-name"
                >
                  {taxonomy.name}
                </label>
                <SortableSelect
                  value={selectedTaxonomyTerms[taxonomySlug]}
                  onChange={(value) => {
                    let termIds = [];

                    if (value) {
                      if (isMultiSelect) {
                        termIds = value.map(({ value }) => value);
                      } else {
                        termIds = [value.value];
                      }
                    }

                    const updatedSelectedTaxonomyTermsIds = {
                      ...selectedTaxonomyTermsIds,
                      [taxonomySlug]: termIds,
                    };

                    const updatedSelectedTaxonomyTerms = {
                      ...selectedTaxonomyTerms,
                      [taxonomySlug]: value,
                    };

                    setSelectedTaxonomyTerms(updatedSelectedTaxonomyTerms);
                    setSelectedTaxonomyTermsIds(
                      updatedSelectedTaxonomyTermsIds,
                    );

                    updateMeta(
                      'selected_taxonomy_terms',
                      updatedSelectedTaxonomyTermsIds,
                    );

                    resetPrimaryTaxonomyTerm(
                      taxonomySlug,
                      updatedSelectedTaxonomyTermsIds,
                    );

                    //update current post taxonomies
                    dispatch('core').editEntityRecord(
                      'postType',
                      postType,
                      currentPostId,
                      {
                        [taxonomySlug]: termIds,
                      },
                    );
                  }}
                  options={taxonomy.terms}
                  isMultiSelect={isMultiSelect}
                />
              </div>

              {primaryTermTaxonomiesForPostType.includes(taxonomySlug) && (
                <div className="b3rg-primary-term" key={primaryTermElementKey}>
                  <label
                    className={'b3rg-primary-term__label'}
                    htmlFor="primary-term"
                  >
                    {`Primary Term - ${taxonomy.name}`}
                  </label>
                  <SortableSelect
                    value={primaryTerms[taxonomySlug]}
                    onChange={(term) => {
                      const updatedSelectedPrimaryTermIds = {
                        ...primaryTermIds,
                        [taxonomySlug]: parseInt(term.value),
                      };

                      const updatedSelectedPrimaryTerms = {
                        ...primaryTerms,
                        [taxonomySlug]: term,
                      };

                      setPrimaryTermIds(updatedSelectedPrimaryTermIds);
                      setPrimaryTerms(updatedSelectedPrimaryTerms);

                      updateMeta(
                        'selected_primary_terms',
                        updatedSelectedPrimaryTermIds,
                      );
                    }}
                    options={
                      selectedTaxonomyTerms[taxonomySlug].length
                        ? selectedTaxonomyTerms[taxonomySlug]
                        : [selectedTaxonomyTerms[taxonomySlug]]
                    }
                    isMultiSelect={false}
                  />
                </div>
              )}
            </>
          );
        })
      : '';

    return (
      <PluginDocumentSettingPanel
        name="tag-reordering"
        title={__('Tag Reordering', '')}
        initialOpen={true}
      >
        {reOrderingTemplate}
      </PluginDocumentSettingPanel>
    );
  };

  const TagReorderingOptions = (props) => {
    const { postType } = props;
    if (!postType || 'page' === postType) {
      return null;
    }
    return <TagReorderingSettings {...props} />;
  };

  return withSelect((select) => {
    const postMeta = select('core/editor').getEditedPostAttribute('meta') || {};
    const postType = select('core/editor').getCurrentPostType();
    return {
      postMeta,
      currentPostId: select('core/editor').getCurrentPostId(),
      postType,
    };
  })(TagReorderingOptions);
}

wp.domReady(() => {
  if (wp.editPost) {
    wp.plugins.registerPlugin('tag-reordering', {
      render: registerTagReorderingPlugin(wp),
      icon: 'editor-table',
    });
  }
});
